import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../components/common/CookiesPopUp';
import AccordionList, {
  IAccordionListItem,
} from '../components/common/blocks/AccordionList';
import ReviewsCarousel, {
  IReviewsCarouselProps,
} from '../components/common/blocks/ReviewsCarousel';
import TextblocksWithIcon, {
  ITextblocksWithIconProps,
} from '../components/common/blocks/TextblocksWithIcon';
import TrustFacts, {
  ITrustFactsProps,
} from '../components/common/blocks/TrustFacts';
import { ITrustedByProps } from '../components/common/blocks/TrustedBy';
import { IFooterProps } from '../components/common/layout/Footer';
import Layout from '../components/common/layout/Layout';
import { INavBarProps } from '../components/common/layout/NavBar';
import { IContactProps } from '../components/contact-us/Contact';
import SellingPoints, {
  ISellingPoints,
} from '../components/country/SellingPoints';
import FeaturedServices, {
  IFeaturedServiceProps,
} from '../components/home/FeaturedServices';
import Hero, { IHeroComponentProps } from '../components/home/Hero';
import ShortcutCountryList, {
  IShortcutCountryList,
} from '../components/home/ShortcutCountryList';
import { PageJSONLDSchema } from '../models/app.models';
import {
  IBusinessKeyValuesComponent,
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICountryRegion,
  ICustomerReview,
  ICustomerReviewsComponent,
  ICustomerReviewsMeta,
  IHomePage,
  ILayoutPageProps,
  IMedia,
  ITextBlockWithIcon,
  ITrademarkRegistrationCountryLanding,
  ITrustedByComponent,
  ITrustFacts,
} from '../models/strapi.models';
import { fetchStrapi } from '../services/api.service';
import { getRoutes } from '../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToElement,
} from '../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getLocalizedSlugByCountryIso,
  getTranslation,
  keepObjectProperties,
} from '../shared/useUtils';
import { IAppPage } from '../store/slices/appSlice';
import { NextPageWithLayout } from './_app';

export interface IHomePageProps extends ILayoutPageProps {
  hero: IHeroComponentProps;
  featuredServices: IFeaturedServiceProps;
  shortcutCountryList: IShortcutCountryList;
  trustedBy: ITrustedByComponent;
  customerReviews: ICustomerReviewsComponent;
  reviews: ICustomerReview[];
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  howItWorks: ITextblocksWithIconProps;
  introductionTitle: string;
  introductionDescription: string;
  introductionImage: IMedia;
  faqsTitle: string;
  faqs: IAccordionListItem[];
  businessKeyValues: IBusinessKeyValuesComponent;
  callToActionWithImage: ICallToActionComponent;
  customerReviewsMeta: ICustomerReviewsMeta;
  customerReviewsTopTipStart: string;
  customerReviewsTopTipEnd: string;
  customerReviewsBottomTip: string;
  trustFactsProps: ITrustFactsProps;
}

const HomePage: NextPageWithLayout<IHomePageProps> = (
  props: IHomePageProps
) => {
  const router = useRouter();
  const [hydrated, setHydrated] = useState(false);

  const heroProps: IHeroComponentProps = {
    title: props.hero.title,
    description: props.hero.description,
    buttonLabel: props.hero.buttonLabel,
    buttonAction: () => scrollToElement('contact'),
    backgroundUrl:
      props.hero.backgroundUrl || '/assets/images/home/map-image.png',
    titleHighlightedText: props.hero.titleHighlightedText,
    backgroundAlt: props.hero.backgroundAlt || 'Hero Background Image',
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || ''}
        />
      ),
    })),
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const reviewsCarouselProps: IReviewsCarouselProps = {
    averageScore: props.customerReviewsMeta.average,
    totalReviews: props.customerReviewsMeta.numberOfReviews,
    topTipStart: props.customerReviewsTopTipStart,
    topTipEnd: props.customerReviewsTopTipEnd,
    title: props.customerReviews.title,
    description: props.customerReviews.description,
    bottomTip: props.customerReviewsBottomTip,
    defaultLinkPath: props.customerReviews.buttonPath,
    reviews: props.reviews.map((review) => ({
      text: review.content,
      username: review.customerName,
      score: 5,
      date: formatDate(review.date),
      link: review.link,
    })),
  };

  const businessKeyValueProps: ISellingPoints = {
    title: props.businessKeyValues.title,
    description: props.businessKeyValues.description,
    sellingPointsItems: [
      {
        title: props.businessKeyValues.keyValueOneTitle,
        description: props.businessKeyValues.keyValueOneDescription,
        iconPath: '/assets/images/icons/ExpertAgentIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueTwoTitle,
        description: props.businessKeyValues.keyValueTwoDescription,
        iconPath: '/assets/images/icons/WorldWideServicesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueThreeTitle,
        description: props.businessKeyValues.keyValueThreeDescription,
        iconPath: '/assets/images/icons/NoHiddenChargesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueFourTitle,
        description: props.businessKeyValues.keyValueFourDescription,
        iconPath: '/assets/images/icons/FreeConsultIcon.svg',
      },
    ],
  };

  const callToActionWithImageComponentProps = {
    title: props.callToActionWithImage?.title || '',
    content: props.callToActionWithImage?.content || '',
    image: props.callToActionWithImage?.image!,
    buttons:
      props.callToActionWithImage?.buttons.map((button, index) => {
        if (index === 0) {
          button.action = () => scrollToElement('contact');
        } else if (index === 1) {
          button.action = () => scrollToElement('services');
        }
        return button;
      }) || [],
  };

  const TrustedByComponent = dynamic(
    () => import('../components/common/blocks/TrustedBy')
  );
  const ContactComponent = dynamic(
    () => import('../components/contact-us/Contact')
  );
  const CallToActionWithImageComponent = dynamic(
    () => import('../components/common/blocks/CallToActionWithImage')
  );

  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <meta
          name="facebook-domain-verification"
          content="v1rp4jxjfkqwderk1ntxo8bjfy51hz"
        />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <Hero {...heroProps} />
        </div>
        <div className="page-content-block">
          <TrustFacts {...props.trustFactsProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <FeaturedServices {...props.featuredServices} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <TextblocksWithIcon {...props.howItWorks} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <div className="two-columns">
            <div className="min-height-image-container">
              <Image
                src={getImageSrcWithFallback(props.introductionImage, 'large')}
                alt={
                  props.introductionImage.alternativeText ||
                  props.introductionTitle
                }
                fill
                sizes="(max-width: 768px) 100vw, 75vw"
                quality={90}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className="paragraphs-lighter">
              <h2>{props.introductionTitle}</h2>
              <ReactMarkdown>{props.introductionDescription}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <ShortcutCountryList {...props.shortcutCountryList} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <SellingPoints {...businessKeyValueProps} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          {hydrated && <ReviewsCarousel {...reviewsCarouselProps} />}
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <TrustedByComponent {...trustedByProps} />
        </div>
      </div>

      {props.faqs && props.faqs.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <AccordionList title={props.faqsTitle || ''} items={props.faqs} />
          </div>
        </div>
      )}

      <div className="page-content-section is-narrower">
        <div className="page-content-block">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-narrow background-light">
        <div className="page-content-block">
          <CallToActionWithImageComponent
            {...callToActionWithImageComponentProps}
          />
        </div>
      </div>
    </div>
  );
};

HomePage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: IHomePageProps }> {
  const appRoutes = await getRoutes();

  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const pageUrlParams = {
    'populate[0]': 'meta',
    'populate[1]': 'hero',
    'populate[2]': 'hero.background',
    'populate[3]': 'featuredServices',
    'populate[4]': 'introductionTitle',
    'populate[5]': 'introductionDescription',
    'populate[6]': 'introductionImage',
    'populate[7]': 'businessKeyValues',
    'populate[8]': 'howItWorksTitle',
    'populate[9]': 'howItWorks',
    'populate[10]': 'howItWorks.icon',
    'populate[11]': 'quickAccessTitle',
    'populate[12]': 'quickAccessSubTitle',
    'populate[13]': 'quickAccessShowAllButton',
    'populate[14]': 'trustedBy',
    'populate[15]': 'trustedBy.logos',
    'populate[16]': 'customerReviews',
    'populate[17]': 'contactUsForm',
    'populate[18]': 'contactUsFormSuccessMessage',
    'populate[19]': 'contactUsFormErrorMessage',
    'populate[20]': 'faqsTitle',
    'populate[21]': 'faqs',
    'populate[22]': 'callToActionWithImage',
    'populate[23]': 'callToActionWithImage.buttons',
    'populate[24]': 'callToActionWithImage.image',
    'populate[25]': 'featuredServices.serviceOneIcon',
    'populate[26]': 'featuredServices.serviceTwoIcon',
    'populate[27]': 'featuredServices.serviceThreeIcon',
  };

  const regionsUrlParams = {
    'populate[0]': 'nameTranslations',
    'populate[1]': 'featuredCountries',
    'populate[2]': 'featuredCountries.nameTranslations',
  };

  const trademarkLandingUrlParams = new URLSearchParams({
    'populate[0]': 'country',
    'populate[1]': 'metas',
    'populate[2]': 'meta',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  // Get page data
  const page = (
    await fetchStrapi<any>(`/home-page`, pageUrlParams, buildApiKey)
  ).data.attributes as IHomePage;
  const regions = flatStrapiCollection(
    await fetchStrapi<any>(`/country-regions`, regionsUrlParams, buildApiKey)
  ) as ICountryRegion[];

  regions.forEach((x) => {
    if (
      x.featuredCountries &&
      (x.featuredCountries as any).data &&
      (x.featuredCountries as any).data.length > 0
    ) {
      x.featuredCountries = flatStrapiCollection(x.featuredCountries as any);
    } else {
      x.featuredCountries = [];
    }
  });

  const featuredCountriesIds = regions
    .map((x) => x.featuredCountries?.map((y) => y.id))
    .flat();
  featuredCountriesIds.forEach((id: number | undefined) => {
    trademarkLandingUrlParams.append(
      `filters[country][id][$in]`,
      id!.toString()
    );
  });

  const trademarkLandingsResult = await fetchStrapi<any>(
    `/trademark-registration-landings`,
    trademarkLandingUrlParams,
    buildApiKey
  );
  const trademarkLandings: ITrademarkRegistrationCountryLanding[] =
    flatStrapiCollection<ITrademarkRegistrationCountryLanding>(
      trademarkLandingsResult
    );

  if (trademarkLandings.length > 0) {
    trademarkLandings.forEach((x) => {
      x.country = flatStrapiObject(x.country as any);
    });
  }

  const reviews = flatStrapiCollection(
    await fetchStrapi<any>(
      `/customer-reviews`,
      customerReviewsUrlParams,
      buildApiKey
    )
  ) as ICustomerReview[];
  const trustedBy: ITrustedByComponent = getTranslation(
    page.trustedBy,
    context.locale
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );
  const contactMethods = await getContactMethods();
  const customerReviews: ICustomerReviewsComponent = getTranslation(
    page.customerReviews,
    context.locale
  );

  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;

  // Check if customer reviews title contains placecholders. Example: "Average rating: {{average}} based on {{numberOfReviews}} reviews (Best rating: {{bestRating}})"
  if (
    customerReviews.title.includes('{{') &&
    customerReviews.title.includes('}}')
  ) {
    customerReviews.title = customerReviews.title.replace(
      '{{average}}',
      customerReviewsMeta.average.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{numberOfReviews}}',
      customerReviewsMeta.numberOfReviews.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{bestRating}}',
      customerReviewsMeta.bestRating.toString()
    );
  }

  // Prepare Hero
  const heroData = getTranslation(page.hero, context.locale);
  const heroProps: IHeroComponentProps = {
    title: heroData.title,
    description: heroData.description,
    buttonLabel: heroData.buttonLabel,
    buttonPath: heroData.buttonPath || '',
    secondaryButtonLabel: heroData.secondaryButtonLabel,
    backgroundUrl:
      getImageSrcWithFallback(flatStrapiObject(heroData.background), 'full') ||
      '/assets/images/home/map-image.png',
    titleHighlightedText: heroData.highlightedTitleText,
    backgroundAlt:
      flatStrapiObject<any>(heroData.background).alternativeText ||
      'Hero Background Image',
  };

  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  // Clean up data
  regions.forEach((x) => {
    x.countries = [];
  });

  const trademarkLandingsMinimalData = trademarkLandings.map((x) => ({
    country: { iso: x.country.iso },
    metas: x.meta.filter((y) => y.language === context.locale) || [],
  }));

  // Prepare countries list
  const getShortcutCountryListFeaturedCountries = (region: ICountryRegion) => {
    const featuredCountries = region.featuredCountries || [];
    return featuredCountries
      .map((c) => {
        const trademarkLandingSlug = getLocalizedSlugByCountryIso(
          trademarkLandingsMinimalData,
          c.iso,
          context.locale || 'en'
        );
        return {
          name: getTranslation(c.nameTranslations, context.locale || 'en')
            .text as string,
          link: getLocalizedAppRoute(
            `/trademark-registration-services/${trademarkLandingSlug}`
          ),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const quickAccess = {
    title: getTranslation(page.quickAccessTitle, context.locale).text as string,
    subTitle: getTranslation(page.quickAccessSubTitle, context.locale)
      .text as string,
    showAllLabel: getTranslation(page.quickAccessShowAllButton, context.locale)
      .text as string,
    regions,
  };

  const shortcutCountryListProps: IShortcutCountryList = {
    title: quickAccess.title,
    description: quickAccess.subTitle,
    showAllLabel: quickAccess.showAllLabel,
    regions: quickAccess.regions
      .map((x) => ({
        name: getTranslation(x.nameTranslations, context.locale || 'en')
          .text as string,
        link: getLocalizedAppRoute('/countries'),
        countries: getShortcutCountryListFeaturedCountries(x),
        order: x.order,
      }))
      .sort((a, b) => a.order - b.order),
  };

  // Prepare Featured Services
  const featuredServices = getTranslation(
    page.featuredServices,
    context.locale
  );
  const featuredServicesProps: IFeaturedServiceProps = {
    title: featuredServices.title,
    description: featuredServices.description,
    services: [
      {
        title: featuredServices.serviceOneTitle,
        description: featuredServices.serviceOneDescription,
        iconPath:
          getImageSrcWithFallback(
            flatStrapiObject(featuredServices.serviceOneIcon),
            'full'
          ) || '/assets/images/icons/TrademarkServices.svg',
        link: featuredServices.serviceOnePath,
      },
      {
        title: featuredServices.serviceTwoTitle,
        description: featuredServices.serviceTwoDescription,
        iconPath:
          getImageSrcWithFallback(
            flatStrapiObject(featuredServices.serviceTwoIcon),
            'full'
          ) || '/assets/images/icons/CopyrightServices.svg',
        link: featuredServices.serviceTwoPath,
      },
      ...(featuredServices.serviceThreeTitle &&
      featuredServices.serviceThreeDescription
        ? [
            {
              title: featuredServices.serviceThreeTitle,
              description: featuredServices.serviceThreeDescription,
              iconPath:
                getImageSrcWithFallback(
                  flatStrapiObject(featuredServices.serviceThreeIcon),
                  'full'
                ) || '/assets/images/icons/IndustrialDesignServices.svg',
              link: featuredServices.serviceThreePath,
            },
          ]
        : []),
    ],
  };

  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);
  const commonTranslations = await getCommonTranslations();

  const faqsTitle =
    page.faqsTitle && page.faqsTitle.length > 0
      ? (getTranslation(page.faqsTitle, context.locale).text as string)
      : (getTranslation(commonTranslations.faqsTitle, context.locale)
          .text as string);
  const faqs = page?.faqs
    ?.filter((x) => x.language === context.locale)
    .map((x) => ({
      title: x.question,
      content: x.answer,
    })) as IAccordionListItem[];

  const callToActionWithImage = getTranslation(
    page.callToActionWithImage,
    context.locale
  );

  if (
    callToActionWithImage.image &&
    (callToActionWithImage.image as any).data
  ) {
    callToActionWithImage.image = flatStrapiObject(callToActionWithImage.image);
  }

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...customerReviewsSchema],
  };

  const howItWorks: ITextblocksWithIconProps = {
    title: getTranslation(page.howItWorksTitle, context.locale).text as string,
    items: (page.howItWorks || [])
      .filter((x) => x.language === context.locale)
      .map((x: ITextBlockWithIcon) => ({
        content: x.content,
        icon: flatStrapiObject(x.icon as any) as IMedia,
      })),
  };

  const trustFacts: ITrustFacts = getTranslation(
    commonTranslations.trustFactsStrip,
    context.locale
  );

  const trustFactsProps: ITrustFactsProps = {
    locale: context.locale,
    facts: (trustFacts?.facts || []).map((x) => ({
      value: x.value,
      description: x.description,
    })),
  };

  const props: IHomePageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    hero: heroProps,
    featuredServices: featuredServicesProps,
    shortcutCountryList: shortcutCountryListProps,
    trustedBy,
    customerReviews,
    reviews,
    contactMethods,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage: getTranslation(
      page.contactUsFormSuccessMessage,
      context.locale
    ).text as string,
    contactUsFormErrorMessage: getTranslation(
      page.contactUsFormErrorMessage,
      context.locale
    ).text as string,
    alternativeLinks,
    canonicalLink,
    introductionTitle:
      getTranslation(page.introductionTitle, context.locale).text || '-',
    introductionDescription:
      getTranslation(page.introductionDescription, context.locale).text || '-',
    introductionImage: flatStrapiObject(page.introductionImage),
    businessKeyValues: getTranslation(page.businessKeyValues, context.locale),
    howItWorks,
    faqsTitle,
    faqs,
    callToActionWithImage,
    customerReviewsMeta,
    customerReviewsTopTipStart: getTranslation(
      commonTranslations.customerReviewsTopTipStart,
      context.locale
    ).text as string,
    customerReviewsTopTipEnd: getTranslation(
      commonTranslations.customerReviewsTopTipEnd,
      context.locale
    ).text as string,
    customerReviewsBottomTip: getTranslation(
      commonTranslations.customerReviewsBottomTip,
      context.locale
    ).text as string,
    trustFactsProps,
  };

  return {
    props,
  };
}

export default HomePage;
